import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  BookOpen, GraduationCap, Stethoscope, Cpu, Activity, ChevronRight,
  CheckCircle2, Clock, Users, Award, ArrowRight
} from "lucide-react";

const courses = [
  {
    id: "11th",
    icon: <BookOpen size={32} color="#F2C94C" />,
    title: "11th Science",
    subtitle: "Foundation Year",
    badge: "HSC Board",
    badgeColor: "#0A2540",
    description:
      "The critical foundation year for science stream students. We build strong conceptual foundations in all core subjects, preparing students for both board exams and future entrance preparations.",
    subjects: ["Physics", "Chemistry", "Mathematics", "Biology"],
    highlights: [
      "Complete NCERT + Board syllabus coverage",
      "Regular chapter-wise tests",
      "Doubt clearing sessions",
      "Study material provided",
      "Performance tracking",
    ],
    duration: "10–12 months",
    batchSize: "Small batches",
    exams: ["HSC Board (Maharashtra)"],
  },
  {
    id: "12th",
    icon: <GraduationCap size={32} color="#F2C94C" />,
    title: "12th Science",
    subtitle: "Board + Entrance",
    badge: "HSC + Entrance",
    badgeColor: "#0A2540",
    description:
      "Comprehensive coaching covering the HSC board syllabus while simultaneously preparing students for NEET, JEE, and MHT-CET. The most critical year for science students.",
    subjects: ["Physics", "Chemistry", "Mathematics", "Biology"],
    highlights: [
      "Dual focus: Board + Competitive exams",
      "Full syllabus coverage with revision",
      "Mock test series",
      "Previous year paper practice",
      "One-on-one mentoring",
    ],
    duration: "12 months",
    batchSize: "Small batches",
    exams: ["HSC Board", "NEET", "JEE Main", "MHT-CET"],
  },
  {
    id: "neet",
    icon: <Stethoscope size={32} color="#F2C94C" />,
    title: "NEET Preparation",
    subtitle: "Medical Entrance",
    badge: "Medical",
    badgeColor: "#166534",
    description:
      "Specialized NEET coaching with a strong focus on Biology, Physics, and Chemistry. Our NEET students have consistently secured admissions to top medical colleges across Maharashtra and India.",
    subjects: ["Biology (Botany + Zoology)", "Physics", "Chemistry"],
    highlights: [
      "NCERT-focused approach",
      "Chapter-wise and full-length mock tests",
      "Previous 10 years paper analysis",
      "Biology special sessions",
      "Revision before exam",
    ],
    duration: "12–24 months",
    batchSize: "Small batches",
    exams: ["NEET UG"],
  },
  {
    id: "jee",
    icon: <Cpu size={32} color="#F2C94C" />,
    title: "JEE Main & Advanced",
    subtitle: "Engineering Entrance",
    badge: "Engineering",
    badgeColor: "#1e40af",
    description:
      "Rigorous JEE coaching focusing on deep problem-solving skills, conceptual understanding, and exam strategy. Our JEE students have achieved excellent percentile rankings.",
    subjects: ["Physics", "Chemistry", "Mathematics"],
    highlights: [
      "JEE Main + Advanced syllabus",
      "Advanced problem-solving sessions",
      "Weekly mock tests with analysis",
      "Formula and concept revision",
      "Past paper practice",
    ],
    duration: "12–24 months",
    batchSize: "Small batches",
    exams: ["JEE Main", "JEE Advanced"],
  },
  {
    id: "mhtcet",
    icon: <Activity size={32} color="#F2C94C" />,
    title: "MHT-CET Preparation",
    subtitle: "State Entrance Exam",
    badge: "Maharashtra",
    badgeColor: "#9a3412",
    description:
      "Focused MHT-CET preparation aligned with Maharashtra State Board syllabus. Ideal for students targeting engineering and pharmacy colleges in Maharashtra.",
    subjects: ["Physics", "Chemistry", "Mathematics (PCM)", "Biology (PCB)"],
    highlights: [
      "Maharashtra Board aligned syllabus",
      "PCM and PCB group options",
      "CET-pattern mock tests",
      "State board-specific approach",
      "Time management strategies",
    ],
    duration: "6–12 months",
    batchSize: "Small batches",
    exams: ["MHT-CET (Engineering)", "MHT-CET (Pharmacy/Medical)"],
  },
];

export default function Courses() {
  const [active, setActive] = useState("11th");
  const course = courses.find((c) => c.id === active);

  return (
    <div data-testid="courses-page" className="pt-16 md:pt-20">
      {/* Header */}
      <section className="page-header py-14 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs font-bold tracking-widest uppercase mb-3" style={{ color: "#F2C94C" }}>
            Programs Offered
          </p>
          <h1
            className="text-4xl md:text-6xl font-extrabold text-white mb-4"
            style={{ fontFamily: "Manrope, sans-serif" }}
          >
            Our Courses
          </h1>
          <p className="text-blue-200 text-base md:text-lg max-w-2xl mx-auto">
            Choose from our comprehensive range of science programs designed for HSC students and entrance exam aspirants.
          </p>
          <nav className="flex items-center justify-center gap-2 mt-6 text-xs text-blue-300">
            <Link to="/" className="hover:text-yellow-400 transition-colors">Home</Link>
            <ChevronRight size={12} />
            <span className="text-yellow-400">Courses</span>
          </nav>
        </div>
      </section>

      {/* Course selector tabs */}
      <section className="bg-white border-b border-gray-100 sticky top-16 md:top-20 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex overflow-x-auto gap-1 py-3 scrollbar-none">
            {courses.map((c) => (
              <button
                key={c.id}
                onClick={() => setActive(c.id)}
                data-testid={`course-tab-${c.id}`}
                className={`flex-shrink-0 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                  active === c.id
                    ? "text-white"
                    : "text-gray-500 hover:text-[#0A2540] hover:bg-gray-50"
                }`}
                style={active === c.id ? { backgroundColor: "#0A2540" } : {}}
              >
                {c.title}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Course Detail */}
      <section data-testid="course-detail-section" className="py-16 md:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main info */}
            <div className="lg:col-span-2 space-y-6">
              <div className="bg-white rounded-2xl p-8 border border-gray-100 shadow-sm">
                <div className="flex items-start gap-4 mb-6">
                  <div
                    className="w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: "#0A2540" }}
                  >
                    {course.icon}
                  </div>
                  <div>
                    <div className="flex items-center gap-3 mb-1">
                      <h2
                        className="text-2xl md:text-3xl font-extrabold"
                        style={{ color: "#0A2540", fontFamily: "Manrope, sans-serif" }}
                      >
                        {course.title}
                      </h2>
                      <span
                        className="text-xs font-bold px-3 py-1 rounded-full text-white"
                        style={{ backgroundColor: course.badgeColor }}
                      >
                        {course.badge}
                      </span>
                    </div>
                    <p className="text-gray-500 text-sm">{course.subtitle}</p>
                  </div>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed mb-6">{course.description}</p>

                {/* Course highlights */}
                <h3
                  className="font-bold text-base mb-4"
                  style={{ color: "#0A2540", fontFamily: "Manrope, sans-serif" }}
                >
                  Course Highlights
                </h3>
                <div className="space-y-3">
                  {course.highlights.map((h, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <CheckCircle2 size={16} color="#F2C94C" className="flex-shrink-0" />
                      <span className="text-sm text-gray-600">{h}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Subjects */}
              <div className="bg-white rounded-2xl p-8 border border-gray-100 shadow-sm">
                <h3
                  className="font-bold text-lg mb-4"
                  style={{ color: "#0A2540", fontFamily: "Manrope, sans-serif" }}
                >
                  Subjects Covered
                </h3>
                <div className="flex flex-wrap gap-3">
                  {course.subjects.map((s, i) => (
                    <span
                      key={i}
                      className="px-4 py-2 rounded-full text-sm font-semibold"
                      style={{ backgroundColor: "#f0f7ff", color: "#0A2540" }}
                    >
                      {s}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-5">
              {/* Quick info */}
              <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
                <h3
                  className="font-bold text-base mb-4"
                  style={{ color: "#0A2540", fontFamily: "Manrope, sans-serif" }}
                >
                  Course Details
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Clock size={16} color="#F2C94C" />
                    <div>
                      <div className="text-xs text-gray-400">Duration</div>
                      <div className="text-sm font-semibold" style={{ color: "#0A2540" }}>
                        {course.duration}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Users size={16} color="#F2C94C" />
                    <div>
                      <div className="text-xs text-gray-400">Batch Type</div>
                      <div className="text-sm font-semibold" style={{ color: "#0A2540" }}>
                        {course.batchSize}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Award size={16} color="#F2C94C" className="mt-0.5" />
                    <div>
                      <div className="text-xs text-gray-400">Target Exams</div>
                      <div className="space-y-1 mt-1">
                        {course.exams.map((e, i) => (
                          <div key={i} className="text-sm font-semibold" style={{ color: "#0A2540" }}>
                            {e}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* CTA */}
              <div
                className="rounded-2xl p-6 text-white"
                style={{ background: "linear-gradient(135deg, #0A2540, #0d3362)" }}
              >
                <h3
                  className="font-bold text-lg mb-2"
                  style={{ fontFamily: "Manrope, sans-serif" }}
                >
                  Interested in this course?
                </h3>
                <p className="text-blue-200 text-sm mb-4">
                  Get a free counseling session and know if this course is right for you.
                </p>
                <Link
                  to="/contact"
                  data-testid="course-enquire-btn"
                  className="flex items-center justify-center gap-2 w-full py-3 rounded-xl font-bold text-sm transition-all hover:opacity-90"
                  style={{ backgroundColor: "#F2C94C", color: "#0A2540" }}
                >
                  Enquire Now <ArrowRight size={14} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* All courses grid */}
      <section data-testid="all-courses-section" className="py-14 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2
            className="text-2xl font-extrabold mb-8 text-center"
            style={{ color: "#0A2540", fontFamily: "Manrope, sans-serif" }}
          >
            All Programs at a Glance
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {courses.map((c) => (
              <button
                key={c.id}
                onClick={() => {
                  setActive(c.id);
                  window.scrollTo({ top: 200, behavior: "smooth" });
                }}
                data-testid={`course-overview-${c.id}`}
                className="rounded-2xl p-5 text-left border transition-all hover:shadow-md"
                style={
                  active === c.id
                    ? { backgroundColor: "#0A2540", borderColor: "#0A2540" }
                    : { backgroundColor: "white", borderColor: "#e2e8f0" }
                }
              >
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center mb-3"
                  style={{ backgroundColor: active === c.id ? "#F2C94C20" : "#0A2540" }}
                >
                  {React.cloneElement(c.icon, { color: active === c.id ? "#F2C94C" : "#F2C94C" })}
                </div>
                <div
                  className="font-bold text-sm"
                  style={{ color: active === c.id ? "white" : "#0A2540" }}
                >
                  {c.title}
                </div>
                <div
                  className="text-xs mt-1"
                  style={{ color: active === c.id ? "#93c5fd" : "#94a3b8" }}
                >
                  {c.subtitle}
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
